import { FC } from 'react';
import { SkeletonText, Toggle } from '@carbon/react';
import { Link } from 'components/link';
import routes from '~/routes';
import { NotificationRow } from './notification-row';
import { groupByDay } from './group-by-day';
import type { NotificationRow as Row } from './types';
import styles from './notification-feed.module.scss';

interface NotificationPanelProps {
  rows: Row[];
  isLoading: boolean;
  unreadCount: number;
  totalCount: number;
  doNotDisturb: boolean;
  onDoNotDisturbChange: (enabled: boolean) => void;
  unreadOnly: boolean;
  onUnreadOnlyChange: (enabled: boolean) => void;
  onMarkRead: (id: number) => void;
  onMarkAllRead: () => void;
  onClose: () => void;
}

/**
 * The dropdown under the bell.
 *
 * Shows read and unread together rather than unread only: the common question on opening it
 * is "what happened recently", and a panel that empties itself as you read it gives you no
 * way back to the thing you just clicked past.
 *
 * @return {JSX.Element} The dropdown: header actions, grouped rows, and its footer.
 */
export const NotificationPanel: FC<NotificationPanelProps> = ({
  rows,
  isLoading,
  unreadCount,
  totalCount,
  doNotDisturb,
  onDoNotDisturbChange,
  unreadOnly,
  onUnreadOnlyChange,
  onMarkRead,
  onMarkAllRead,
  onClose,
}) => {
  const groups = groupByDay(rows);

  return (
    <div className={styles.panel} role="dialog" aria-label="Notifications">
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>Notifications</h2>
        {/*
          "Dismiss all" rather than "Mark all read": it is the same operation, but the word
          people reach for when they want the badge gone is dismiss. Nothing is deleted —
          the rows stay readable below and on the full page.
        */}
        {unreadCount > 0 ? (
          <button
            type="button"
            className={styles.panelAction}
            onClick={onMarkAllRead}
          >
            Mark all as read
          </button>
        ) : null}
      </div>

      {/*
        Two switches in one row: what the panel shows, and whether the badge shouts about it.
        Unread-only is a view filter and does not touch what is recorded — same as the Read
        State filter on the full page, so the two surfaces answer the same question the same
        way.
      */}
      <div className={styles.panelToggleRow}>
        <Toggle
          id="notifications-unread-only"
          size="sm"
          labelA="Unread only"
          labelB="Unread only"
          labelText=""
          toggled={unreadOnly}
          onToggle={onUnreadOnlyChange}
        />
        <Toggle
          id="notifications-do-not-disturb"
          size="sm"
          labelA="Do not disturb"
          labelB="Do not disturb"
          labelText=""
          toggled={doNotDisturb}
          onToggle={onDoNotDisturbChange}
        />
      </div>

      {doNotDisturb ? (
        <p className={styles.panelNote}>
          The badge is hidden on this browser. Notifications are still being
          recorded and will be here when you come back.
        </p>
      ) : null}

      <div className={styles.panelBody} role="list">
        {isLoading && rows.length === 0 ? (
          <div className={styles.panelPlaceholder}>
            <SkeletonText paragraph lineCount={3} />
          </div>
        ) : null}

        {!isLoading && rows.length === 0 ? (
          <p className={styles.panelEmpty}>
            {unreadOnly
              ? 'Nothing unread.'
              : 'You will hear about notifications on items you watch when there are changes.'}
          </p>
        ) : null}

        {groups.map((group) => (
          <div key={group.label}>
            {/*
              Sticky so the heading stays visible while its own rows scroll past, which is
              what makes the grouping useful in a 24rem-tall panel rather than decorative.
            */}
            <div className={styles.groupHeading}>{group.label}</div>

            {group.rows.map((row) => (
              <NotificationRow
                key={row.id}
                row={row}
                onSelect={(selected) => {
                  // Reading it is implied by opening it; doing both in one click is the
                  // whole point of the row being a link.
                  if (!selected.read) onMarkRead(selected.id);
                  onClose();
                }}
              />
            ))}
          </div>
        ))}
      </div>

      <div className={styles.panelFooter}>
        <Link to={routes.notificationsPath} onClick={onClose}>
          View all{totalCount > 0 ? ` (${totalCount})` : ''}
        </Link>
      </div>
    </div>
  );
};
